import React, { useCallback, useEffect } from 'react';
import { useLocalStorage } from 'react-use';
import slugify from 'slugify';
import styled, { css } from 'styled-components';
import { Link } from 'wouter';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { EntityWithLabel, WorkspaceLiteData } from '../../../api/ApiTypes';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { TextButton } from '../../../ui/TextButton';
import { AppLocation, AppLocationLayer } from '../../AppLocation';
import { useResolve } from '../../Container';
import { ShowIfHasFeature } from '../../common/FeatureElements';
import { usePermissionCheck } from '../../common/PermissionElements';
import { HelpController } from '../../core/help/HelpController';
import { HelpTip, HelpTipMount } from '../../core/help/HelpTip';
import { Copy, Title } from '../../dashboard/elements/LayoutElements';
import { workspaceJoin } from '../../workspace/WorkspaceCommands';
import { AccountModel } from '../AccountModel';
import { NavigationModel } from './NavigationModel';

const unpackWorkspaces = (data: WorkspaceLiteData[]) => {
    const defaultSpace = data.find(x => x.type === 'default');
    const librarySpace = data.find(x => x.type === 'library');
    const userSpace = data.find(x => x.type === 'user');
    const teamSpaces = data.filter(x => x.type === 'team').sort((a, b) => a.label.localeCompare(b.label));
    return { defaultSpace, librarySpace, userSpace, teamSpaces };
};

// 1C50B7
const Frame = styled.div`
`;

const Section = styled.div`
    padding: 1rem 0 .5rem 0;
    /* border-bottom: solid 1px #eee; */
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 0rem 0.5rem .375rem 1.7rem;

    ${DropButton} {
        margin-left: auto;
        margin-right: .5rem;

        &:hover {
            color: ${Colors.primaryText}
        }
    }
`;

const ItemDisc = styled.div<{ active?: boolean }>`
    display: inline-block;
    width: 1em;
    height: 1em;
    border: solid 2px #fff;
    border-radius: 50%;
    margin-right: 1rem;
    margin-left: 0.1rem;

    ${p => p.active ? 'background: white;' : ''}
`;

const ItemLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ItemActions = styled.div`
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 1.1rem;
    opacity: 0;
    pointer-events: none;
    transition: opacity 200ms ease-out;

    ${DropButton} {
        &:hover {
            color: ${Colors.primaryText};
        }
    }
`;

const ItemGroup = styled.div`
    margin-bottom: .5rem;

    &:last-child {
        margin-bottom: 0;
    }

    &:hover ${ItemActions} {
        opacity: 1; 
        pointer-events: all;
        transition: none;
    }
`;

const ItemFrame = styled.div`
    position: relative;
`;

const Item = styled.div<{ active?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    padding: .375rem 3rem .375rem 3.5rem;
    cursor: pointer;
    user-select: none;
 
    ${p => !p.active ? '' : css`
        background: rgba(0,0,0,.2);
    `}
    &:hover {
        background: rgba(0,0,0,.1);
    }
    &:active {
        background: rgba(0,0,0,.2);
    }

    svg, ${ItemDisc} {
        position: absolute;
        left: 2.25rem;
        transform: translateX(-50%);
    }

    ${ItemDisc} {
        transform: translateX(-50%) translateX(-.1rem);
    }
`;

const SubItem = styled(Item)`
    padding: .5rem 3rem .5rem 5.25rem;
    font-size: .875rem;
`;

const ItemPill = styled.div`
    margin-left: auto;
    margin-right: 0.75rem;
    font-size: .875rem;
`;

const MessageItem = styled.div<{ lite?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    margin: .5rem 1rem;
    border-radius: .25rem;
    user-select: none; 
    background: rgba(255,255,255,0.1);   

    ${TextButton} {
        text-decoration: underline;
        color: white;
    }

    ${Title}, ${Copy} {
        color: white;
    }

    ${p => p.lite ? css`
        background: rgba(255,255,255,0.1);
        color: rgba(255,255,255,0.75);
    ` : ''}
`;

const ordered: <T extends EntityWithLabel>(list: T[]) => T[] =
    (list) => list.slice().sort((a, b) => a.label.localeCompare(b.label));

const WorkspaceItem = React.memo<{ workspace: WorkspaceLiteData, layers: AppLocationLayer[] }>(({ workspace, layers }) => {
    const [collapsed, setCollapsed] = useLocalStorage(`deqo/ui/${workspace.id}/collapsed`, false);
    const onToggle = useCallback(() => setCollapsed(!collapsed), [collapsed]);

    return <ItemGroup key={workspace.id}>
        <ItemFrame>
            <Link data-testid={`nav-workspace-${slugify(workspace.label)}`} to={`~/${workspace.id}`}>
                <Item active={layers[0]?.value === workspace.id && (collapsed || !layers.find(x => x.name === 'folder'))}>
                    <ItemDisc active={layers[0]?.value === workspace.id} />
                    <ItemLabel>{workspace.label}</ItemLabel>
                </Item>
            </Link>
            <ItemActions>
                {!!workspace.folders.length && <DropButton overflow="0" padding=".25" onClick={onToggle}>
                    <Icon size="1" src={`deqo://icons/hero/chevron-${collapsed ? 'left' : 'down'}`} />
                </DropButton>}
            </ItemActions>
        </ItemFrame>
        {
            !collapsed && ordered(workspace.folders).map(f =>
                <ItemFrame key={f.id}>
                    <Link to={`~/${f.id}`}>
                        <SubItem active={layers[1]?.value === f.id && layers.length >= 2}>
                            <ItemLabel>{f.label}</ItemLabel>
                        </SubItem>
                    </Link>
                </ItemFrame>
            )
        }
    </ItemGroup >;
});

const legacyBoardsHelpTip = HelpTip(
    'legacyNav',
    'legacyNav',
    'Looking for your v1 boards?',
    'In here you can find all your legacy boards as well as the ability to import them into v2!',
);

export const MainMenuSlice = () => {

    const dispatch = useResolve(CommandDispatcher);
    const layers = useObserver(useResolve(AppLocation));
    const { legacy, createdAt } = useObserver(useResolve(AccountModel));
    const { workspaces } = useObserver(useResolve(NavigationModel));
    const { defaultSpace, librarySpace, userSpace, teamSpaces } = unpackWorkspaces(workspaces);
    const readonly = usePermissionCheck('da:write');
    const help = useResolve(HelpController);

    useEffect(() => {
        if (legacy && createdAt >= new Date(2023, 8, 1)) {
            const t = setTimeout(() => {
                help.openTip(legacyBoardsHelpTip);
            }, 500);
            return () => {
                clearTimeout(t);
                help.dismissTip(legacyBoardsHelpTip);
            };
        }
    }, [legacy]);

    return <Frame>
        <Section>
            <Link to="~/boards">
                {defaultSpace && <Item data-testid="nav-boards" active={['boards', defaultSpace.id].includes(layers[0]?.value)}>
                    <Icon size="1.2" src="deqo://icons/dash/board" /> All Boards
                </Item>}
            </Link>
            <ShowIfHasFeature name="workspaces">
                {userSpace && <Link to={`~/${userSpace.id}`}>
                    <Item data-testid="nav-private" active={layers[0]?.value === userSpace.id}>
                        <Icon size="1.2" src="deqo://icons/hero/user" /> Private Boards
                    </Item>
                </Link>}
            </ShowIfHasFeature>
            {legacy &&
                <HelpTipMount name="legacyNav" placement="right" highlight>
                    <Link to="~/legacy">
                        <Item data-testid="nav-legacy" active={layers[0]?.name === 'legacy'}>
                            <Icon size="1.2" src="deqo://icons/hero/archive" /> Legacy Boards
                        </Item>
                    </Link>
                </HelpTipMount>
            }
            {librarySpace && <Link to="~/library">
                <Item data-testid="nav-templates" active={layers[0]?.value === 'library'}>
                    <Icon size="1.2" src="deqo://icons/hero/template" /> Template Library
                </Item>
            </Link>}
            <Link to="~/tutorials">
                <Item data-testid="nav-tutorials" active={layers[0]?.name === 'tutorials'}>
                    <Icon size="1.2" src="deqo://icons/i8/tuition" /> Tutorials &amp; Help
                </Item>
            </Link>
        </Section>
        <Section>
            <SectionHeader>
                Workspaces
                {readonly &&
                    <DropButton data-testid="nav-workspaces-add" rounding="full" padding=".375" onClick={() => dispatch(workspaceJoin)}>
                        <Icon size="1" src="deqo://icons/deqo/add" />
                    </DropButton>
                }
            </SectionHeader>
            {teamSpaces.length > 0 && <>
                {ordered(teamSpaces).map(w => <WorkspaceItem key={w.id} workspace={w} layers={layers} />)}
            </>}
            {teamSpaces.length === 0 && <>
                <MessageItem lite>
                    You don&apos;t have any workspaces yet.
                </MessageItem>
            </>}
        </Section>
    </Frame>;
};

